// The arrears ladder's building blocks: the ladder strip, the action timeline and the three
// dialogs (send a reminder with its letter, log a promise, add a note). Used by the Arrears page
// drawer and the tenancy page's arrears banner.
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CalendarCheck, Check, Mail, MessageSquareText, NotebookPen, Phone, ScrollText, ShieldCheck } from 'lucide-react';
import { Badge, Button, Field, Input, Modal, Select, Textarea } from '../ui';
import { FormError, toMinor } from '../forms/common';
import { formatDate, formatMoney } from '../../lib/viz';
import { arrearsApi, daysColour, type ArrearsAction, type ArrearsDetail, type Channel } from '../../lib/arrears';

const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDays = (n: number) => { const d = new Date(); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

/** The four rungs: sent (when), due now, reached, or not reached yet. Tap one to write that letter. */
export function LadderStrip({ detail, onPick }: { detail: ArrearsDetail; onPick?: (step: number) => void }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {detail.ladder.map((s) => {
        const tone = s.sent ? 'border-emerald-200 bg-emerald-50/70' : s.due ? 'border-amber-300 bg-amber-50' : s.reached ? 'border-ink-200 bg-white' : ' border-ink-200 bg-ink-50/60';
        return (
          <button key={s.step} type="button" disabled={!onPick || !!s.sent || !s.reached} onClick={() => onPick?.(s.step)}
            className={`min-w-0 rounded-xl border px-3 py-2.5 text-left transition enabled:hover:border-ink-400 ${tone}`}>
            <div className="flex items-center justify-between gap-2">
              <b className="text-[13px] text-ink-900">{s.days} days</b>
              {s.sent ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : s.due ? <span className="h-2 w-2 rounded-full bg-amber-500" /> : null}
            </div>
            <div className="truncate text-[11.5px] text-ink-600">{s.label}</div>
            <div className={`mt-0.5 truncate text-[10.5px] ${s.sent ? 'text-emerald-700' : s.due ? 'font-medium text-amber-700' : 'text-ink-400'}`}>
              {s.sent ? `${s.sent.channel === 'DRAFT' ? 'drafted' : 'sent'} ${formatDate(s.sent.at)}` : s.due ? (detail.row?.paused ? 'due — paused' : 'ready to send') : s.reached ? 'passed' : 'not reached'}
            </div>
          </button>
        );
      })}
    </div>
  );
}

const ICON: Record<string, React.ComponentType<{ className?: string }>> = { REMINDER: Mail, PROMISE: CalendarCheck, NOTE: NotebookPen, CALL: Phone, NOTICE: ScrollText };

export function actionTitle(a: ArrearsAction): string {
  if (a.clears) return a.note ?? 'Arrears cleared';
  switch (a.kind) {
    case 'REMINDER': return `${a.stepLabel} ${a.channel === 'DRAFT' ? 'written (draft)' : 'sent'}`;
    case 'PROMISE': return `Promised to pay${a.promisedAmountMinor ? ` ${formatMoney(a.promisedAmountMinor, a.currency)}` : ''} by ${formatDate(a.promisedOn!)}`;
    case 'CALL': return 'Phone call';
    case 'NOTICE': return 'Notice served';
    default: return 'Note';
  }
}

/** Newest first: every reminder, promise, note, call and the receipt that cleared it. */
export function ActionTimeline({ actions }: { actions: ArrearsAction[] }) {
  if (!actions.length) return <p className="py-3 text-[12.5px] text-ink-400">Nothing done yet. Reminders, promises and notes appear here.</p>;
  return (
    <ol className="relative space-y-3.5 border-l border-ink-100 pl-5">
      {actions.map((a) => {
        const I = a.clears ? ShieldCheck : ICON[a.kind] ?? NotebookPen;
        return (
          <li key={a.id} className="relative">
            <span className={`absolute -left-[29px] top-0.5 grid h-[18px] w-[18px] place-items-center rounded-full ring-4 ring-white ${a.clears ? 'bg-emerald-100 text-emerald-700' : a.kind === 'REMINDER' ? 'bg-amber-100 text-amber-700' : a.kind === 'PROMISE' ? 'bg-sky-100 text-sky-700' : 'bg-ink-100 text-ink-600'}`}>
              <I className="h-3 w-3" />
            </span>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <span className="text-[13px] font-medium text-ink-900">{actionTitle(a)}</span>
              <span className="text-[11px] text-ink-400">{formatDate(a.createdAt)}{a.createdByName ? ` · ${a.createdByName}` : a.clears ? '' : ' · system'}</span>
            </div>
            {a.kind === 'REMINDER' && (
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11.5px] text-ink-500">
                {a.outboxStatus && <Badge value={a.outboxStatus} />}
                {a.owedMinor !== null && <span>{formatMoney(a.owedMinor, a.currency)} owed · {a.daysOverdue} days</span>}
                {Array.isArray(a.meta.to) && <span className="truncate">→ {(a.meta.to as Array<string | null>).filter(Boolean).join(', ')}</span>}
              </div>
            )}
            {a.note && !a.clears && <p className="mt-0.5 whitespace-pre-wrap text-[12.5px] text-ink-600">{a.note}</p>}
          </li>
        );
      })}
    </ol>
  );
}

/** Pick a rung, read the letter that will go (rendered from the template), send it or leave it as a draft. */
export function ReminderDialog({ detail, initialStep, onClose, onDone }: { detail: ArrearsDetail; initialStep?: number; onClose: () => void; onDone: (msg: string) => void }) {
  const row = detail.row;
  const reached = detail.ladder.filter((s) => s.reached && !s.sent && s.step > (row?.lastStepSent ?? 0));
  const [step, setStep] = useState<number>(initialStep ?? row?.nextStepDue ?? reached[reached.length - 1]?.step ?? 1);
  const [channel, setChannel] = useState<Channel>('EMAIL');
  const preview = useQuery({ queryKey: ['/arrears/preview', detail.tenancyId, step], queryFn: () => arrearsApi.preview(detail.tenancyId, step) });
  const send = useMutation({
    mutationFn: () => arrearsApi.remind(detail.tenancyId, { step, channel }),
    onSuccess: (r) => onDone(r.sent ? `${r.step.label} sent to ${r.letters.filter((l) => l.status === 'SENT').map((l) => l.toEmail).join(', ')}` : `${r.step.label} saved to the outbox as a draft`),
  });
  const p = preview.data;
  const noEmail = !!p && !p.recipients.some((r) => r.email);
  return (
    <Modal open onClose={onClose} title="Send a reminder" size="lg"
      footer={<div className="flex w-full flex-wrap items-center justify-end gap-2">
        <Select value={channel} onChange={(e) => setChannel(e.target.value as Channel)} className="!py-2 text-[12.5px] sm:mr-auto" aria-label="How">
          <option value="EMAIL">Send by email now</option>
          <option value="DRAFT">Leave as a draft in the outbox</option>
        </Select>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button icon="Send" disabled={!p || p.alreadySent || send.isPending || !reached.some((s) => s.step === step)} onClick={() => send.mutate()}>
          {send.isPending ? 'Sending…' : channel === 'EMAIL' ? `Send ${p?.step.label.toLowerCase() ?? 'reminder'}` : 'Save draft'}
        </Button>
      </div>}>
      <div className="space-y-3">
        {row && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-ink-100 bg-ink-50/60 px-3.5 py-2.5 text-[12.5px]">
            <b className="text-ink-900">{formatMoney(row.owedMinor, row.currency)}</b>
            <span style={{ color: daysColour(row.daysOverdue) }} className="font-medium">{row.daysOverdue} days late</span>
            <span className="text-ink-500">{row.instalments} instalment{row.instalments === 1 ? '' : 's'} · since {formatDate(row.oldestDueDate)}</span>
          </div>
        )}
        <div className="flex flex-wrap gap-1.5">
          {detail.ladder.map((s) => {
            const can = s.reached && !s.sent && s.step > (row?.lastStepSent ?? 0);
            return (
              <button key={s.step} type="button" disabled={!can} onClick={() => setStep(s.step)}
                className={`h-7 rounded-lg border px-2.5 text-[12px] transition disabled:opacity-40 ${step === s.step ? 'border-bronze-500 bg-bronze-50 text-bronze-700' : 'border-ink-200 bg-white text-ink-600 hover:bg-ink-50'}`}>
                {s.days}d · {s.label}{s.sent ? ' ✓' : ''}
              </button>
            );
          })}
        </div>
        {row?.paused && <p className="rounded-xl border border-sky-200 bg-sky-50 px-3.5 py-2.5 text-[12.5px] text-sky-800">They promised to pay by {formatDate(row.promise!.promisedOn)}; the ladder is paused until {formatDate(row.pausedUntil!)}. You can still send one by hand.</p>}
        {preview.isLoading ? <div className="h-64 animate-pulse rounded-xl bg-ink-100" /> : preview.error ? <FormError error={preview.error} /> : p && (
          <div className="overflow-hidden rounded-xl border border-ink-200">
            <div className="space-y-1 border-b border-ink-100 bg-ink-50/60 px-4 py-2.5 text-[12px]">
              <div className="flex gap-2"><span className="w-14 shrink-0 text-ink-400">To</span><span className="min-w-0 break-words text-ink-800">{p.recipients.map((r) => r.email ? `${r.name ?? ''} <${r.email}>` : `${r.name ?? 'Tenant'} (no email)`).join(', ') || 'Nobody on the tenancy'}</span></div>
              <div className="flex gap-2"><span className="w-14 shrink-0 text-ink-400">Subject</span><span className="min-w-0 break-words font-medium text-ink-900">{p.subject}</span></div>
            </div>
            <pre data-testid="letter" className="max-h-[42vh] overflow-auto whitespace-pre-wrap break-words px-4 py-3 font-sans text-[12.5px] leading-relaxed text-ink-800">{p.body}</pre>
          </div>
        )}
        {p?.alreadySent && <p className="text-[12.5px] text-red-600">This step has already been sent.</p>}
        {!!p?.missing.length && <p className="text-[12.5px] text-amber-700">Not filled: {p.missing.map((m) => `{{${m}}}`).join(', ')} — it will be saved as a draft to finish in the Outbox.</p>}
        {noEmail && <p className="text-[12.5px] text-amber-700">Nobody on the tenancy has an email address — the letter will be saved as a draft.</p>}
        <FormError error={send.error} />
      </div>
    </Modal>
  );
}

export function PromiseDialog({ detail, onClose, onDone }: { detail: ArrearsDetail; onClose: () => void; onDone: (msg: string) => void }) {
  const [on, setOn] = useState(plusDays(3));
  const [amount, setAmount] = useState(detail.row ? (detail.row.owedMinor / 100).toFixed(2) : '');
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () => arrearsApi.promise(detail.tenancyId, { promisedOn: on, amountMinor: amount ? toMinor(amount) ?? null : null, note: note || null }),
    onSuccess: (a) => onDone(`Promise to pay by ${formatDate(a.promisedOn!)} logged — reminders paused`),
  });
  return (
    <Modal open onClose={onClose} title="Log a promise to pay"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button disabled={!on || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Log promise'}</Button></>}>
      <div className="space-y-3">
        <p className="text-[12.5px] leading-relaxed text-ink-500">The ladder pauses until two days after the promised date. If the money is not in by then, the next reminder becomes due again and the promise shows as missed.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Pay by"><Input type="date" value={on} min={todayISO()} onChange={(e) => setOn(e.target.value)} className="w-full" /></Field>
          <Field label="Amount (£)" hint="Leave blank if they did not say"><Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full" /></Field>
        </div>
        <Field label="Note"><Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Who you spoke to, how, anything agreed…" className="w-full" /></Field>
        <FormError error={save.error} />
      </div>
    </Modal>
  );
}

export function NoteDialog({ detail, onClose, onDone }: { detail: ArrearsDetail; onClose: () => void; onDone: (msg: string) => void }) {
  const [kind, setKind] = useState<'NOTE' | 'CALL' | 'NOTICE'>('NOTE');
  const [note, setNote] = useState('');
  const save = useMutation({ mutationFn: () => arrearsApi.note(detail.tenancyId, { kind, note }), onSuccess: () => onDone(kind === 'CALL' ? 'Call logged' : kind === 'NOTICE' ? 'Notice recorded' : 'Note added') });
  return (
    <Modal open onClose={onClose} title="Add to the arrears history"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button disabled={!note.trim() || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Add'}</Button></>}>
      <div className="space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {([['NOTE', 'Note', NotebookPen], ['CALL', 'Phone call', Phone], ['NOTICE', 'Notice served', ScrollText]] as const).map(([k, l, I]) => (
            <button key={k} type="button" onClick={() => setKind(k)} className={`inline-flex h-7 items-center gap-1.5 rounded-lg border px-2.5 text-[12px] ${kind === k ? 'border-bronze-500 bg-bronze-50 text-bronze-700' : 'border-ink-200 bg-white text-ink-600 hover:bg-ink-50'}`}><I className="h-3.5 w-3.5" />{l}</button>
          ))}
        </div>
        <Textarea rows={5} value={note} onChange={(e) => setNote(e.target.value)} placeholder={kind === 'CALL' ? 'What was said…' : kind === 'NOTICE' ? 'Which notice, how it was served, when it expires… (recording only — nothing is sent)' : 'Payment plan agreed, who you spoke to…'} className="w-full" />
        <FormError error={save.error} />
      </div>
    </Modal>
  );
}

export type ArrearsDialog = { kind: 'remind'; step?: number } | { kind: 'promise' } | { kind: 'note' } | null;

/** Renders whichever dialog is open. */
export function ArrearsDialogs({ detail, dialog, onClose, onDone }: { detail: ArrearsDetail; dialog: ArrearsDialog; onClose: () => void; onDone: (msg: string) => void }) {
  if (!dialog) return null;
  if (dialog.kind === 'remind') return <ReminderDialog detail={detail} initialStep={dialog.step} onClose={onClose} onDone={onDone} />;
  if (dialog.kind === 'promise') return <PromiseDialog detail={detail} onClose={onClose} onDone={onDone} />;
  return <NoteDialog detail={detail} onClose={onClose} onDone={onDone} />;
}

export const ConversationIcon = MessageSquareText;
